import { useEffect, useState } from 'react';

import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, ImageList, ImageListItem, ImageListItemBar,
    Rating, Stack, TextField, Typography
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import RemoveSharpIcon from '@mui/icons-material/RemoveSharp';
import SaveIcon from '@mui/icons-material/Save';

import {
    reviewErrorText,
    saveCustomerReview
} from '../../../../actions/review';
import { msg } from '../../../../actions/swal_msg';

const MAX_IMAGES = 6;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const ReviewModal = ({
    action,
    onClose,
    onSaved,
    open,
    productId,
    product,
    review,
    variant,
    initialRating = 0
}) => {
    const [comment, setComment] = useState('');
    const [rating, setRating] = useState(0);
    const [images, setImages] = useState([]);
    const [newImages, setNewImages] = useState([]);
    const [removedImageIds, setRemovedImageIds] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open)
            return;

        setComment(review ? review.comment : '');
        setRating(review ? review.rating : initialRating);
        setImages(review ? review.images || [] : []);
        setNewImages([]);
        setRemovedImageIds([]);
    }, [initialRating, open, review]);

    const loadImages = ({ target }) => {
        const selected = [...target.files];
        const available = MAX_IMAGES - images.length - newImages.length;

        if (selected.some(file => !IMAGE_TYPES.includes(file.type))) {
            msg('warning', 'Imágenes',
                'Sólo se permiten imágenes JPEG, PNG o WebP');
            target.value = '';
            return;
        }

        if (selected.some(file => file.size > MAX_IMAGE_SIZE)) {
            msg('warning', 'Imágenes',
                'Cada imagen debe pesar como máximo 5 MB');
            target.value = '';
            return;
        }

        if (available < 1) {
            msg('warning', 'Imágenes', 'Puedes incluir hasta 6 imágenes');
            target.value = '';
            return;
        }

        const accepted = selected.slice(0, available).map(file => ({
            file,
            preview: URL.createObjectURL(file)
        }));
        setNewImages(current => [...current, ...accepted]);

        if (accepted.length < selected.length)
            msg('warning', 'Imágenes', 'Sólo se agregaron las primeras 6 imágenes');

        target.value = '';
    };

    const removeStoredImage = image => {
        setImages(current => current.filter(item => item.id !== image.id));
        setRemovedImageIds(current => [...current, image.id]);
    };

    const removeNewImage = image => {
        URL.revokeObjectURL(image.preview);
        setNewImages(current => current.filter(item => item.preview !== image.preview));
    };

    const closeModal = () => {
        for (const image of newImages)
            URL.revokeObjectURL(image.preview);

        onClose();
    };

    const submit = async () => {
        const normalizedComment = comment.trim();

        if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
            msg('warning', 'Reseña', 'Selecciona una calificación de 1 a 5 estrellas');
            return;
        }

        if (normalizedComment.length < 6 || normalizedComment.length > 2000) {
            msg('warning', 'Reseña',
                'El comentario debe tener entre 6 y 2000 caracteres');
            return;
        }

        setLoading(true);

        try {
            const body = await saveCustomerReview({
                reviewId: review ? review.id : '',
                productId,
                variantId: variant._id,
                rating,
                comment: normalizedComment,
                images: newImages.map(image => image.file),
                removedImageIds
            });

            if (!body.status) {
                msg('warning', 'Reseña', reviewErrorText(body.msg));
                return;
            }

            msg('success', action === 'edit' ?
                'Reseña actualizada'
            :
                'Reseña publicada',
            'Gracias por compartir tu experiencia');
            onSaved(body.resp);
            closeModal();
        } catch (error) {
            msg('error', 'Reseña', 'No se pudo guardar la reseña');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={loading ? undefined : closeModal}
        >
            <DialogTitle>
                {action === 'edit' ? 'Editar reseña' : 'Crear reseña'}
            </DialogTitle>
            <DialogContent>
                {product &&
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        {product.image_url &&
                            <Box
                                component="img"
                                src={product.image_url}
                                alt={product.title}
                                sx={{
                                    borderRadius: 1,
                                    height: 72,
                                    objectFit: 'contain',
                                    width: 72
                                }}
                            />
                        }
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {product.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {variant.variant_title || 'Presentación única'}
                            </Typography>
                        </Box>
                    </Stack>
                }
                {review?.status === 'hidden' &&
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Puedes editar esta reseña, pero seguirá oculta hasta que
                        el equipo de Nefrópolis la restaure.
                    </Alert>
                }
                {!product &&
                    <Typography variant="body2" color="text.secondary">
                        Variante: {
                            variant.variant_title || 'Presentación única'
                        }
                    </Typography>
                }
                <Typography component="legend" sx={{ mt: 3 }}>
                    Calificación
                </Typography>
                <Rating
                    name="rating"
                    value={rating}
                    onChange={(event, value) => setRating(value || 0)}
                />
                <TextField
                    sx={{ mt: 2 }}
                    fullWidth
                    label="Comentario sobre el producto"
                    multiline
                    minRows={4}
                    inputProps={{ maxLength: 2000 }}
                    value={comment}
                    onChange={({ target }) => setComment(target.value)}
                    helperText={`${comment.trim().length}/2000 caracteres`}
                />

                <Box sx={{ mt: 3 }}>
                    <input
                        accept="image/jpeg,image/png,image/webp"
                        id="review-images"
                        multiple
                        style={{ display: 'none' }}
                        type="file"
                        onChange={loadImages}
                    />
                    <label htmlFor="review-images">
                        <Button variant="outlined" component="span">
                            Agregar imágenes
                        </Button>
                    </label>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Hasta 6 imágenes JPEG, PNG o WebP de 5 MB cada una.
                    </Typography>
                </Box>

                <ImageList cols={3} rowHeight={180}>
                    {images.map(image =>
                        <ImageListItem key={image.id}>
                            <img src={image.url} alt={image.alt} loading="lazy" />
                            <ImageListItemBar actionIcon={
                                <IconButton
                                    sx={{ color: 'rgba(255,255,255,.8)' }}
                                    onClick={() => removeStoredImage(image)}
                                    aria-label="Retirar imagen"
                                >
                                    <RemoveSharpIcon />
                                </IconButton>
                            } />
                        </ImageListItem>
                    )}
                    {newImages.map(image =>
                        <ImageListItem key={image.preview}>
                            <img src={image.preview} alt={image.file.name} />
                            <ImageListItemBar actionIcon={
                                <IconButton
                                    sx={{ color: 'rgba(255,255,255,.8)' }}
                                    onClick={() => removeNewImage(image)}
                                    aria-label="Retirar imagen"
                                >
                                    <RemoveSharpIcon />
                                </IconButton>
                            } />
                        </ImageListItem>
                    )}
                </ImageList>
            </DialogContent>
            <DialogActions>
                <Button onClick={closeModal} disabled={loading}>Cancelar</Button>
                <LoadingButton
                    loading={loading}
                    loadingPosition="start"
                    startIcon={<SaveIcon />}
                    onClick={submit}
                >
                    {action === 'edit' ? 'Actualizar' : 'Publicar'}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ReviewModal;
